// Time tracking for a project task (tasks-clickup-overhaul P3).
// Start/stop timer + manual entries, total logged vs. estimate. Self-contained:
// loads + mutates via the time store, holds its own local state. A live elapsed
// tick runs only while the current user has a running timer on this task.

import { useEffect, useRef, useState } from "preact/hooks";
import { Pause, Play, Plus, Trash2 } from "lucide-preact";
import type { TaskTimeSummary } from "@juuplan/shared";
import { addManualTime, deleteTimeEntry, fetchTaskTime, startTimer, stopTimer } from "../store/time";
import { findUser } from "../store/users";
import { useMe } from "../lib/auth";
import { toastError } from "../lib/toast";
import { BotBadgeFor } from "./BotBadge";

interface Props {
  taskId: number;
  /** Estimated hours (numeric string) for the "X von Y h" comparison. */
  estimatedHours?: string | null;
  readOnly?: boolean;
}

/** "1h 30m" / "45m" / "0m" from minutes. */
function fmt(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
}

export function TimeTrackingSection(props: Props): preact.JSX.Element {
  const { taskId, estimatedHours, readOnly = false } = props;
  const me = useMe();
  const [summary, setSummary] = useState<TaskTimeSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState("");
  const [, forceTick] = useState(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Request-id guard: switching tasks mid-request must not let the previous
  // task's summary resolve later and overwrite the current one.
  // Der leere Reject-Handler `() => {}` liess `summary` auf null stehen — die
  // Anzeige stand dann auf 0:00, als waere keine Zeit erfasst.
  //
  // Und die Folge davon war schlimmer als die falsche Zahl: `running` leitet
  // sich aus `summary` ab, bei null zeigte der Knopf „Timer starten" — AUCH
  // wenn serverseitig einer laeuft. Ein Klick haette dann einen ZWEITEN Timer
  // gestartet. Deshalb ist der Knopf bei Ladefehler gesperrt: eine Aktion auf
  // Basis unbekannten Zustands anzubieten ist schlechter, als sie kurz zu
  // verweigern. Der Fehlerhinweis mit „Erneut laden" steht direkt darunter.
  const [loadError, setLoadError] = useState(false);
  const loadSeqRef = useRef(0);
  const load = (): void => {
    const seq = ++loadSeqRef.current;
    setLoadError(false);
    void fetchTaskTime(taskId).then(
      (s) => {
        if (loadSeqRef.current === seq) setSummary(s ?? null);
      },
      () => {
        if (loadSeqRef.current !== seq) return;
        setLoadError(true);
        toastError("Zeiterfassung konnte nicht geladen werden.");
      }
    );
  };
  useEffect(load, [taskId]);

  // Live elapsed tick only while a timer runs.
  useEffect(() => {
    const running = summary?.running;
    if (running) {
      tickRef.current = setInterval(() => forceTick((n) => n + 1), 1000);
      return () => {
        if (tickRef.current) clearInterval(tickRef.current);
      };
    }
    return undefined;
  }, [summary?.running?.id]);

  const running = summary?.running ?? null;
  const total = summary?.total_minutes ?? 0;
  // Number.isFinite guards against a non-numeric estimate rendering "von NaNm".
  const estMinRaw = estimatedHours != null && estimatedHours !== "" ? Math.round(Number(estimatedHours) * 60) : null;
  const estMin = estMinRaw != null && Number.isFinite(estMinRaw) ? estMinRaw : null;

  // Elapsed of the running timer (added on top of the logged total for display).
  const runningElapsed = running
    ? Math.max(0, Math.floor((Date.now() - new Date(running.started_at).getTime()) / 60000))
    : 0;
  const liveTotal = total + runningElapsed;

  const toggleTimer = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      if (running) await stopTimer(taskId);
      else await startTimer(taskId);
      load();
    } catch {
      toastError("Timer-Aktion fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const submitManual = async (): Promise<void> => {
    const minutes = parseManual(manual);
    if (minutes == null || busy) return;
    setBusy(true);
    try {
      await addManualTime(taskId, minutes);
      setManual("");
      load();
    } catch {
      toastError("Zeiteintrag fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entryId: number): Promise<void> => {
    try {
      await deleteTimeEntry(taskId, entryId);
      load();
    } catch {
      toastError("Löschen fehlgeschlagen");
    }
  };

  return (
    <div>
      <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
        Zeiterfassung
      </h3>

      {loadError && (
        <p class="mb-2 text-xs text-[color:var(--color-error)]">
          Zeiterfassung konnte nicht geladen werden — die Summe unten ist
          womöglich nicht aktuell.{" "}
          <button type="button" onClick={load} class="underline">Erneut laden</button>
        </p>
      )}

      <div class="flex items-center gap-3 mb-2">
        {!readOnly && (
          <button
            type="button"
            onClick={() => void toggleTimer()}
            disabled={busy || loadError}
            title={
              loadError
                ? "Zeiterfassung konnte nicht geladen werden — der Timer-Zustand ist unbekannt. Bitte erneut laden."
                : running
                  ? "Laufenden Timer stoppen"
                  : "Timer starten"
            }
            class={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium min-h-[32px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-50 transition-colors ${
              running
                ? "bg-[color:var(--color-prio-urgent)] text-[color:var(--color-on-accent)]"
                : "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)]"
            }`}
          >
            {running ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
            {loadError ? "Timer-Zustand unbekannt" : running ? "Timer stoppen" : "Timer starten"}
          </button>
        )}
        <span class="text-sm tabular-nums text-[color:var(--color-on-surface)]">
          <span class="font-semibold">{fmt(liveTotal)}</span>
          {estMin != null && <span class="text-[color:var(--color-on-surface-variant)]"> von {fmt(estMin)}</span>}
          {running && <span class="ml-2 text-label text-[color:var(--color-prio-urgent)] animate-pulse">läuft…</span>}
        </span>
      </div>

      {estMin != null && estMin > 0 && (
        <div class="mb-2 h-1.5 rounded-full bg-[color:var(--color-surface-container-high)] overflow-hidden">
          <div
            class="h-full transition-[width]"
            style={{
              width: `${Math.min(100, Math.round((liveTotal / estMin) * 100))}%`,
              background: liveTotal > estMin ? "var(--color-prio-urgent)" : "var(--color-primary)",
            }}
          />
        </div>
      )}

      {summary && summary.entries.length > 0 && (
        <ul class="flex flex-col gap-0.5 mb-2">
          {summary.entries.map((e) => {
            const user = findUser(e.user_id);
            const mine = me.user?.id === e.user_id;
            return (
              <li key={e.id} class="group flex items-center gap-2 py-0.5 text-xs">
                <span class="tabular-nums font-medium text-[color:var(--color-on-surface)] w-16">
                  {e.duration_minutes != null ? fmt(e.duration_minutes) : "läuft…"}
                </span>
                <span class="text-[color:var(--color-on-surface-variant)] truncate flex-1 min-w-0 inline-flex items-center gap-1">
                  <span class="truncate">
                    {user?.name ?? `User #${e.user_id}`}
                    {e.note ? ` · ${e.note}` : ""}
                    {e.source === "manual" ? " · manuell" : ""}
                  </span>
                  <BotBadgeFor userId={e.user_id} />
                </span>
                {!readOnly && mine && e.ended_at && (
                  <button
                    type="button"
                    onClick={() => void remove(e.id)}
                    class="shrink-0 inline-flex items-center justify-center size-6 rounded text-[color:var(--color-on-surface-variant)] opacity-0 group-hover:opacity-100 hover:text-[color:var(--color-error)] focus:outline-none focus:opacity-100"
                    aria-label="Zeiteintrag löschen"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitManual();
          }}
          class="flex items-center gap-2"
        >
          <Plus size={14} class="text-[color:var(--color-on-surface-variant)] shrink-0" aria-hidden="true" />
          <input
            type="text"
            value={manual}
            onInput={(e) => setManual((e.target as HTMLInputElement).value)}
            placeholder="Zeit erfassen, z. B. 90 oder 1h 30m"
            aria-label="Zeit manuell erfassen"
            disabled={busy}
            class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          />
          <button
            type="submit"
            disabled={busy || parseManual(manual) == null}
            class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50"
          >
            Hinzufügen
          </button>
        </form>
      )}
    </div>
  );
}

/** Parse "90", "1h 30m", "2h", "45m" → minutes; null when nothing valid. */
export function parseManual(input: string): number | null {
  const s = input.trim().toLowerCase();
  if (!s) return null;
  // plain number → minutes
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n > 0 && n <= 24 * 60 ? n : null;
  }
  const hm = s.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?$/);
  if (hm && (hm[1] || hm[2])) {
    const mins = (Number(hm[1] ?? 0) * 60) + Number(hm[2] ?? 0);
    return mins > 0 && mins <= 24 * 60 ? mins : null;
  }
  return null;
}
